import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import API, { getImageUrl } from "@/lib/api";

const fallbackCategories = ["Gamis", "Couple", "Family Set", "Hijab", "Kids", "Men"];
const BRAND_LOGO = getImageUrl("naura-numa/assets/frontend/logo-haniyah-gallery.jpg");

export default function Footer() {
  const { t } = useLanguage();
  const [categories, setCategories] = useState(fallbackCategories.map((name) => ({ name, value: name })));

  useEffect(() => {
    let mounted = true;
    API.get("/categories")
      .then((r) => {
        const rows = Array.isArray(r.data?.categories) ? r.data.categories : [];
        const activeRows = rows
          .filter((cat) => cat?.name)
          .map((cat) => ({ name: cat.name, value: cat.name }));
        if (mounted && activeRows.length) setCategories(activeRows);
      })
      .catch(() => {
        if (mounted) setCategories(fallbackCategories.map((name) => ({ name, value: name })));
      });
    return () => {
      mounted = false;
    };
  }, []);
  
  return (
    <footer className="bg-stone-900 text-stone-300 py-16" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={BRAND_LOGO} alt="Haniyah Gallery" className="w-12 h-12 rounded-full object-cover border border-white/10 bg-white" />
              <h3 className="font-serif text-2xl text-white">Haniyah Gallery</h3>
            </div>
            <p className="text-sm leading-relaxed text-stone-400">
              {t("footer.description")}
            </p>
          </div>
          <div>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">{t("footer.categories")}</h4>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.value || c.name}
                  to={`/products?category=${encodeURIComponent(c.value || c.name)}`}
                  className="inline-flex items-center rounded-full border border-stone-700/80 px-3 py-1.5 text-xs text-stone-400 hover:border-white/30 hover:bg-white/10 hover:text-white transition-colors"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">{t("footer.company")}</h4>
            <div className="space-y-2">
              <Link to="/brands" className="block text-sm text-stone-400 hover:text-white transition-colors">{t("footer.our_brands")}</Link>
              <Link to="/products" className="block text-sm text-stone-400 hover:text-white transition-colors">{t("footer.shop_all")}</Link>
            </div>
          </div>
          <div>
            <h4 className="font-sans text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">{t("footer.support")}</h4>
            <div className="space-y-2">
              <p className="text-sm text-stone-400">{t("footer.customer_service")}</p>
              <p className="text-sm text-stone-400">{t("footer.shipping_info")}</p>
              <p className="text-sm text-stone-400">{t("footer.returns_exchange")}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-stone-800 mt-12 pt-8 text-center">
          <p className="text-xs text-stone-500">&copy; 2026 Haniyah Gallery. {t("footer.rights_reserved")}</p>
        </div>
      </div>
    </footer>
  );
}
